import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, History, ArrowLeftRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, erroApi } from './api';

const NOME_LOJA = { cerroAzul: 'Cerro Azul', doutorUlysses: 'Doutor Ulysses' };

const badgeEstoque = (p) => {
  const total = (p.estoqueCerroAzul ?? null) === null && (p.estoqueDoutorUlysses ?? null) === null
    ? null
    : (p.estoqueCerroAzul || 0) + (p.estoqueDoutorUlysses || 0);
  if (p.status === 'esgotado' || total === 0) return { rotulo: 'Esgotado', classe: 'bg-red-100 text-mk-red' };
  if (total === null) return { rotulo: 'Não controlado', classe: 'bg-slate-100 text-slate-500' };
  if (total <= (p.estoqueMinimo ?? 2)) return { rotulo: 'Estoque baixo', classe: 'bg-amber-100 text-amber-700' };
  return { rotulo: 'Em estoque', classe: 'bg-green-100 text-green-700' };
};

export default function Estoque() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [mov, setMov] = useState(null);
  const [movForm, setMovForm] = useState({ tipo: 'entrada', loja: 'doutorUlysses', quantidade: '', motivo: '' });
  const [historicoDe, setHistoricoDe] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-estoque', q, page],
    queryFn: async () => {
      const r = await apiFetch(`/admin/produtos?q=${encodeURIComponent(q)}&page=${page}&limit=10`);
      if (!r.ok) throw new Error('falha');
      return r.json();
    },
    refetchOnWindowFocus: true,
  });

  const { data: historico } = useQuery({
    queryKey: ['estoque-historico', historicoDe?.id],
    enabled: Boolean(historicoDe),
    queryFn: async () => (await apiFetch(`/admin/estoque/historico?produtoId=${historicoDe.id}`)).json(),
  });

  const abrirMov = (p) => {
    setMov(p);
    setMovForm({ tipo: 'entrada', loja: 'doutorUlysses', quantidade: '', motivo: '' });
  };

  const confirmarMov = async () => {
    const qtd = parseInt(movForm.quantidade, 10);
    if (isNaN(qtd) || qtd < 0 || (movForm.tipo !== 'ajuste' && qtd <= 0)) {
      toast.error('Informe uma quantidade válida.');
      return;
    }
    const r = await apiFetch(`/admin/produtos/${mov.id}/estoque/movimentacao`, {
      method: 'POST',
      body: JSON.stringify({ tipo: movForm.tipo, loja: movForm.loja, quantidade: qtd, motivo: movForm.motivo }),
    });
    if (r.ok) {
      toast.success('Movimentação registrada.');
      setMov(null);
      qc.invalidateQueries({ queryKey: ['admin-estoque'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
      qc.invalidateQueries({ queryKey: ['estoque-historico'] });
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
  };

  const inputCls = 'mt-1 w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-mk-blue';
  const labelCls = 'text-[11px] font-bold uppercase text-slate-400';

  return (
    <div data-testid="admin-estoque" className="max-w-4xl">
      <h1 className="font-heading text-2xl font-extrabold text-slate-900">Controle de estoque por loja</h1>
      <p className="mt-1 text-sm text-slate-500">Saldos independentes de Cerro Azul e Doutor Ulysses. O total é sempre a soma das duas lojas.</p>

      <div className="relative mt-4 max-w-md">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="estoque-search-input" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Buscar produto..." className="w-full rounded-full border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-mk-blue" />
      </div>

      {isLoading ? (
        <p className="mt-8 text-sm text-slate-400">Carregando...</p>
      ) : (
        <>
          <div className="mt-4 space-y-2">
            {data.items.map((p) => {
              const b = badgeEstoque(p);
              const ca = p.estoqueCerroAzul;
              const du = p.estoqueDoutorUlysses;
              const total = ca === null && du === null ? null : (ca || 0) + (du || 0);
              return (
                <div key={p.id} data-testid={`estoque-row-${p.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white p-3.5 shadow-sm">
                  <img src={p.fotos?.[0]} alt="" className="h-12 w-12 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold leading-tight text-slate-800 line-clamp-2">{p.nome}</p>
                    <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-extrabold ${b.classe}`}>{b.rotulo}</span>
                  </div>
                  <div className="flex items-center gap-3 text-center">
                    <div><p className="text-[10px] font-bold uppercase text-slate-400">Cerro Azul</p><p data-testid={`estoque-ca-${p.id}`} className="font-heading text-lg font-extrabold text-slate-800">{ca ?? '—'}</p></div>
                    <div><p className="text-[10px] font-bold uppercase text-slate-400">Dr. Ulysses</p><p data-testid={`estoque-du-${p.id}`} className="font-heading text-lg font-extrabold text-slate-800">{du ?? '—'}</p></div>
                    <div className="rounded-xl bg-mk-blue/10 px-3 py-1.5"><p className="text-[10px] font-bold uppercase text-mk-blue">Total</p><p data-testid={`estoque-total-${p.id}`} className="font-heading text-lg font-extrabold text-mk-blue">{total ?? '—'}</p></div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`estoque-mov-${p.id}`} onClick={() => abrirMov(p)} className="inline-flex items-center gap-1.5 rounded-full bg-mk-blue px-3.5 py-2 text-xs font-bold text-white hover:bg-mk-bluelight">
                      <ArrowLeftRight size={13} /> Movimentar
                    </button>
                    <button data-testid={`estoque-history-${p.id}`} onClick={() => setHistoricoDe(p)} aria-label="Histórico" className="rounded-xl border border-slate-300 p-2.5 text-slate-500 hover:bg-slate-50"><History size={15} /></button>
                  </div>
                </div>
              );
            })}
            {data.items.length === 0 && <p className="rounded-2xl bg-white py-10 text-center text-sm text-slate-400">Nenhum produto encontrado.</p>}
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full border border-slate-300 bg-white p-2 disabled:opacity-40"><ChevronLeft size={16} /></button>
            <span className="text-sm font-bold text-slate-600">{data.page} / {data.pages}</span>
            <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded-full border border-slate-300 bg-white p-2 disabled:opacity-40"><ChevronRight size={16} /></button>
          </div>
        </>
      )}

      {mov && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-mk-bluedark/70 p-4 backdrop-blur-sm" onClick={() => setMov(null)}>
          <div data-testid="estoque-mov-modal" className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900">Movimentar estoque</h3>
            <p className="mt-0.5 truncate text-sm text-slate-500">{mov.nome}</p>
            <div className="mt-4 space-y-4">
              <div>
                <label className={labelCls}>Tipo de movimentação</label>
                <select data-testid="mov-tipo" value={movForm.tipo} onChange={(e) => setMovForm({ ...movForm, tipo: e.target.value })} className={inputCls}>
                  <option value="entrada">Entrada de mercadoria</option>
                  <option value="saida">Saída de mercadoria</option>
                  <option value="ajuste">Ajuste de inventário (definir quantidade exata)</option>
                  <option value="transferencia">Transferência entre as lojas</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>{movForm.tipo === 'transferencia' ? 'Loja de origem' : 'Loja'}</label>
                <select data-testid="mov-loja" value={movForm.loja} onChange={(e) => setMovForm({ ...movForm, loja: e.target.value })} className={inputCls}>
                  <option value="cerroAzul">Cerro Azul (atual: {mov.estoqueCerroAzul ?? 0})</option>
                  <option value="doutorUlysses">Doutor Ulysses (atual: {mov.estoqueDoutorUlysses ?? 0})</option>
                </select>
                {movForm.tipo === 'transferencia' && (
                  <p className="mt-1 text-xs text-slate-400">
                    Destino: {movForm.loja === 'cerroAzul' ? 'Doutor Ulysses' : 'Cerro Azul'}. A saída da origem e a entrada no destino são registradas juntas.
                  </p>
                )}
              </div>
              <div>
                <label className={labelCls}>Quantidade</label>
                <input data-testid="mov-quantidade" type="number" min="0" value={movForm.quantidade} onChange={(e) => setMovForm({ ...movForm, quantidade: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Motivo (opcional)</label>
                <input value={movForm.motivo} onChange={(e) => setMovForm({ ...movForm, motivo: e.target.value })} className={inputCls} placeholder="Ex.: chegou reposição" />
              </div>
              <div className="flex gap-2">
                <button data-testid="mov-confirm" onClick={confirmarMov} className="flex-1 rounded-full bg-mk-blue py-3 text-sm font-bold text-white hover:bg-mk-bluelight">Confirmar</button>
                <button onClick={() => setMov(null)} className="rounded-full border-2 border-slate-300 px-5 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {historicoDe && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-mk-bluedark/70 p-4 backdrop-blur-sm" onClick={() => setHistoricoDe(null)}>
          <div data-testid="estoque-history-modal" className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900">Histórico de movimentações</h3>
            <p className="mt-0.5 text-sm text-slate-500">{historicoDe.nome}</p>
            <div className="mt-4 space-y-2">
              {(historico || []).length === 0 && <p className="text-sm text-slate-400">Nenhuma movimentação registrada ainda.</p>}
              {(historico || []).map((h, i) => (
                <div key={i} className="rounded-xl bg-slate-50 p-3 text-sm">
                  <p className="font-bold text-slate-800">
                    {h.loja ? `${h.loja}: ` : ''}{h.antes ?? '—'} → {h.depois ?? '—'}
                    {h.tipo && <span className="ml-2 rounded-full bg-mk-blue/10 px-2 py-0.5 text-[10px] font-extrabold uppercase text-mk-blue">{h.tipo}</span>}
                  </p>
                  <p className="text-xs text-slate-500">{h.motivo} · {h.usuario}</p>
                  <p className="text-xs text-slate-400">{new Date(h.data).toLocaleString('pt-BR')}</p>
                </div>
              ))}
            </div>
            <button onClick={() => setHistoricoDe(null)} className="mt-4 w-full rounded-full bg-mk-blue py-2.5 text-sm font-bold text-white">Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
}
